import { addDays, isSameDay, startOfDay, endOfDay, min, max } from 'date-fns';
import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { CaregiverPickerComponent } from '../../../shared/components/caregiver-picker/caregiver-picker.component';
import { CaregiverPickerCompactComponent } from '../../../shared/components/caregiver-picker-compact/caregiver-picker-compact.component';
import { PatientPickerComponent } from '../../../shared/components/patient-picker/patient-picker.component';
import { DashboardStatsComponent } from '../dashboard-stats/dashboard-stats.component';
import { CompanyPickerComponent } from '../../../shared/components/company-picker/company-picker.component';
import { FullCalendarComponent, FullCalendarModule } from '@fullcalendar/angular';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { CommonModule } from '@angular/common';
import { CompanyService } from '../../../services/company.service';
import { AuthService } from '../../../services/auth.service';
import { Appointment } from '../../../shared/models/appointment.model';
import { PatientDto } from '../../../shared/models/patient.model';
import { CaregiverDto } from '../../../shared/models/caregiver.model';
import { AppointmentService } from '../../../services/appointment.service';
import { CalendarOptions } from '@fullcalendar/core/index.js';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { distinctUntilChanged } from 'rxjs';
import { DateUtilsService } from '../../../services/date-utils.service';
import { CardModule } from 'primeng/card';
import { PanelModule } from 'primeng/panel';
import { MessageModule } from 'primeng/message';
import { SelectedVisitDetails } from '../../../shared/models/appointment-check.model';
import { EditCheckInOutModalComponent } from '../edit-check-in-out-modal/edit-check-in-out-modal.component';
import { DatePickerModule } from 'primeng/datepicker';
import { FormsModule } from '@angular/forms';
import { DynamicTextPipe } from '../../../core/pipes/dynamic-text.pipe';
import { PatientPickerCompactComponent } from '../../../shared/components/patient-picker-compact/patient-picker-compact.component';
import { CompanyPickerCompactComponent } from '../../../shared/components/company-picker-compact/company-picker-compact.component';

// Update PLUGINS to remove timeGridPlugin reference
const PLUGINS = [dayGridPlugin, interactionPlugin];
@Component({
  selector: 'app-appointment-weekly-calender',
  imports: [
    CommonModule,
    CaregiverPickerCompactComponent,
    DashboardStatsComponent,
    FullCalendarModule,
    ProgressSpinnerModule,
    CardModule,
    PanelModule,
    MessageModule,
    EditCheckInOutModalComponent,
    DatePickerModule, // <-- Use DatePickerModule instead of CalendarModule
    FormsModule,
    DynamicTextPipe,
    PatientPickerCompactComponent,
    CompanyPickerCompactComponent
  ],
  templateUrl: './appointment-weekly-calender.component.html',
  styleUrl: './appointment-weekly-calender.component.css'
})
export class AppointmentWeeklyCalenderComponent implements OnInit {
  @ViewChild('calendar') calendarComponent!: FullCalendarComponent;

  private readonly authService = inject(AuthService);
  private readonly companyService = inject(CompanyService);
  private readonly appointmentService = inject(AppointmentService);
  private readonly dateUtilsService = inject(DateUtilsService);

  companySkey: string = '';
  appointments: Appointment[] = [];

  selectedPatient: PatientDto | null = null;
  selectedCaregiver: CaregiverDto | null = null;

  isLoading: boolean = false;
  noEvents: boolean = false;

  // Properties for the modal
  displayEditModal: boolean = false;
  selectedVisitDetails: SelectedVisitDetails | null = null;
  startDate: Date = new Date();
  endDate: Date = addDays(new Date(), 7);
  currentDate: Date = new Date();

  calendarOptions: CalendarOptions = {
    plugins: PLUGINS,
    initialView: 'dayGridWeek', // Set week view as default
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: 'dayGridWeek,dayGridDay'
    },
    buttonText: {
      dayGridWeek: 'Week',
      dayGridDay: 'Day',
      today: 'Today'
    },
    height: 'auto',
    contentHeight: 'auto',
    aspectRatio: 1.8,
    events: [],
    eventClick: this.onEventClick.bind(this),
    datesSet: this.handleDatesSet.bind(this),
    firstDay: 0,
    initialDate: new Date(),
    dayMaxEventRows: 3,
    dayMaxEvents: true,
    displayEventEnd: false,
    eventDisplay: 'block',
    eventMouseEnter: (info) => {
      info.el.style.zIndex = '1000';
    },
    eventMouseLeave: (info) => {
      info.el.style.zIndex = 'auto';
    },
    eventContent: (arg: any) => {
      // Helper function to safely format time with fallback
      const safeFormatTime = (dateValue: any, fallbackTime: string = '12:00 AM'): string => {
        try {
          if (!dateValue) return fallbackTime;

          // If it's already a Date object, use it directly
          if (dateValue instanceof Date) {
            return this.dateUtilsService.formatWithTimezone(dateValue, 'hh:mm a');
          }

          // If it's a string, try to parse it
          if (typeof dateValue === 'string') {
            const parsedDate = new Date(dateValue);
            if (!isNaN(parsedDate.getTime())) {
              return this.dateUtilsService.formatWithTimezone(parsedDate, 'hh:mm a');
            }
          }

          return fallbackTime;
        } catch (error) {
          console.warn('Error formatting time:', error, 'Value:', dateValue);
          return fallbackTime;
        }
      };

      // Helper function to calculate end time based on maxHours
      const calculateEndTime = (startTime: any, maxHours: number): string => {
        try {
          if (!startTime) return '11:59 PM';

          const startDate = new Date(startTime);
          if (isNaN(startDate.getTime())) return '11:59 PM';

          // If maxHours is 0, return the same start time
          if (maxHours === 0) {
            return this.dateUtilsService.formatWithTimezone(startDate, 'hh:mm a');
          }

          // Convert maxHours to minutes and add to start time
          const maxMinutes = maxHours * 60;
          const endDate = new Date(startDate.getTime() + maxMinutes * 60000);

          return this.dateUtilsService.formatWithTimezone(endDate, 'hh:mm a');
        } catch (error) {
          console.warn('Error calculating end time:', error);
          return '11:59 PM';
        }
      };

      // Use taskStartTime if available, otherwise fall back to event start
      const displayStartTime = arg.event.extendedProps.taskStartTime
        ? safeFormatTime(arg.event.extendedProps.taskStartTime)
        : safeFormatTime(arg.event.start);

      // Calculate end time based on maxHours and start time
      const endTime =
        arg.event.extendedProps.taskStartTime &&
        arg.event.extendedProps.maxHours !== undefined &&
        arg.event.extendedProps.maxHours !== null
          ? calculateEndTime(arg.event.extendedProps.taskStartTime, arg.event.extendedProps.maxHours)
          : safeFormatTime(arg.event.end, '11:59 PM');

      // Set default styles
      const styles = {
        bg: 'bg-white dark:bg-surface-700',
        border: 'border-gray-300 dark:border-surface-600',
        text: 'text-gray-800 dark:text-surface-50'
      };

      const patientName = arg.event.extendedProps.patientName || 'Unknown Patient';
      const caregiverName = arg.event.extendedProps.caregiverName || 'Unknown Caregiver';

      return {
        html: `
          <div class="p-2 ${styles.bg} border ${styles.border} ${styles.text} rounded-md shadow-sm transition-all hover:shadow-md cursor-pointer">
            <div class="text-xs font-semibold mb-1 truncate" title="Patient: ${patientName}">
              <span class="font-normal mr-1">Patient:</span>
              <i class="text-xs"></i>${patientName}
            </div>
            <div class="text-xs mb-1 truncate" title="Caregiver: ${caregiverName}">
              <span class="font-normal mr-1">Caregiver:</span>
              <i class="text-xs"></i>${caregiverName}
            </div>
            <div class="text-xs font-medium">
              <i class="text-xs"></i>${displayStartTime} - ${endTime}
            </div>
          </div>
        `
      };
    },
    eventDidMount: (info: any) => {
      info.el.style.backgroundColor = 'transparent';
      info.el.style.border = 'none';
      info.el.style.padding = '2px';
    }
  };

  ngOnInit(): void {
    this.companyService
      .getSelectedCompanySkey()
      .pipe(distinctUntilChanged())
      .subscribe((sKey: any) => {
        if (sKey) {
          this.companySkey = sKey;
          this.fetchAppointments();
        }
      });
  }
  handleDatesSet(dateInfo: any) {
    this.startDate = dateInfo.start;
    this.endDate = dateInfo.end;
    if (this.companySkey) {
      this.fetchAppointments();
    }
  }

  fetchAppointments(): void {
    if (!this.companySkey) {
      return;
    }

    this.isLoading = true;

    const query = {
      companyId: this.companySkey,
      fromDate: this.dateUtilsService.getUserLocalDateTime(this.startDate),
      toDate: this.dateUtilsService.getUserLocalDateTime(this.endDate),
      patientId: this.selectedPatient?.sKey,
      caregiverId: this.selectedCaregiver?.sKey
    };

    this.appointmentService.dyDateRangeByCompanyId(query).subscribe({
      next: (response) => {
        this.appointments = response.data || [];
        this.isLoading = false;
        this.noEvents = this.appointments.length === 0;

        this.calendarOptions.events = this.mapAppointmentsToEvents(this.appointments);
      },
      error: (error) => {
        console.error('Error fetching appointments:', error);
        this.isLoading = false;
      }
    });
  }

  private mapAppointmentsToEvents(appointments: Appointment[]): any[] {
    const events = [];

    for (const appointment of appointments) {
      // Use the original UTC or already-converted time, do not convert again
      const startDate = new Date(appointment.appointmentStartDate);
      const endDate = new Date(appointment.appointmentEndDate);

      // Get date-only versions for comparison (removing time component)
      const startDateOnly = startOfDay(startDate);
      const endDateOnly = startOfDay(endDate);

      // Start from the start date
      let current = startDateOnly;

      while (current <= endDateOnly) {
        // Use taskStartTime for the actual start time, fallback to calculated time if not available
        const taskStart = appointment.taskStartTime ? new Date(appointment.taskStartTime) : null;
        const dayStart = isSameDay(current, startDate)
          ? taskStart && isSameDay(current, taskStart)
            ? taskStart
            : startDate
          : current;
        const dayEnd = isSameDay(current, endDateOnly) ? endDate : endOfDay(current);

        // Always include the event for any day within the appointment range
        // This ensures multi-day appointments show on all days including the end date
        events.push({
          title: `${appointment.patientName} - ${appointment.caregiverName}`,
          start: dayStart,
          end: dayEnd,
          extendedProps: {
            appointmentId: appointment.sKey,
            patientName: appointment.patientName,
            caregiverName: appointment.caregiverName,
            taskStartTime: appointment.taskStartTime,
            originalStartTime: appointment.appointmentStartDate,
            originalEndTime: appointment.appointmentEndDate,
            maxHours: appointment.maxHours
          }
        });

        current = addDays(current, 1);
      }
    }
    return events;
  }

  onEventClick(eventInfo: any) {
    // Extract data from the clicked event
    const eventId = eventInfo.event.id;
    const appointmentId = eventInfo.event.extendedProps.appointmentId || eventId;
    const eventStart = eventInfo.event.start; // Use Date objects instead of strings
    const eventEnd = eventInfo.event.end || eventInfo.event.start; // Fallback to start if end is null
    const caregiverName = eventInfo.event.extendedProps.caregiverName;
    const patientName = eventInfo.event.extendedProps.patientName;

    // Use original appointment start/end times from extendedProps if available
    const originalStartTime = eventInfo.event.extendedProps.originalStartTime;
    const originalEndTime = eventInfo.event.extendedProps.originalEndTime;
    const taskStartTime = eventInfo.event.extendedProps.taskStartTime;

    if (!eventStart) {
      console.error('Event start date is missing.');
      return;
    }

    console.log('Event Start:', eventStart);

    // Format the date using local timezone to avoid UTC conversion issues
    // This ensures the date matches exactly what the user clicked on the calendar
    const pad = (n: number) => n.toString().padStart(2, '0');
    const localYear = eventStart.getFullYear();
    const localMonth = eventStart.getMonth() + 1;
    const localDate = eventStart.getDate();
    const eventDate = `${localYear}-${pad(localMonth)}-${pad(localDate)}`;

    console.log('Event Date (Local):', eventDate);
    console.log('Event Start Full:', eventStart);

    // Create the start time for the selected date at start of day in local timezone
    // This ensures we send the correct date with start-of-day time
    const startOfSelectedDay = new Date(localYear, eventStart.getMonth(), localDate, 0, 0, 0, 0);

    // Use the original appointment times or task start time for start/end, but with correct date
    const startTimeToUse = taskStartTime || originalStartTime || startOfSelectedDay.toISOString();
    console.log('Start Time to Use:', startTimeToUse);
    const endTimeToUse =
      originalEndTime || (eventEnd instanceof Date ? eventEnd.toISOString() : startOfSelectedDay.toISOString());

    this.selectedVisitDetails = {
      appointmentId: appointmentId,
      date: eventDate,
      start: startTimeToUse,
      end: endTimeToUse,
      caregiverName: caregiverName,
      patientName: patientName
    };
    this.displayEditModal = true;
  }

  handleModalClose(): void {
    this.displayEditModal = false;
    this.selectedVisitDetails = null;
  }

  handleModalSave(): void {
    this.displayEditModal = false;
    this.selectedVisitDetails = null;
    this.fetchAppointments(); // Refresh calendar data after save
  }
  onPatientChange(event: any): void {
    this.selectedPatient = event;
    if (this.companySkey) {
      this.fetchAppointments();
    }
  }

  onCaregiverChange(event: any): void {
    this.selectedCaregiver = event;
    if (this.companySkey) {
      this.fetchAppointments();
    }
  }

  isSuperAdmin(): boolean {
    return this.authService.isSuperAdmin();
  }

  onDateSelect(date: Date) {
    if (this.calendarComponent) {
      this.calendarComponent.getApi().gotoDate(date);
      this.fetchAppointments();
    }
  }
}
